import Button from '@/shared/Button'
import FormCreator from '@/shared/FormCreator'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'

import { useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import * as Yup from 'yup'

import AddCircle from '@mui/icons-material/AddCircle'
import { ReactComponent as DeleteIcon } from '@/assets/trash_bin.svg'

const mapStateToProps = ({ loading }) => ({ isSubmitting: loading['company/createDomain'] })

const EmailItem = forwardRef(({ index, onDelete, onSubmit }, ref) => {
  const { t } = useTranslation()
  const form = useRef()

  useImperativeHandle(ref, () => ({
    form: form.current,
    submit: async () =>
      new Promise(resolve => {
        form.current.handleSubmit(values => {
          onSubmit?.(values, resolve)
        })()
      }),
  }))

  return (
    <Stack direction={'row'} spacing={2} alignItems={'flex-start'}>
      <FormCreator
        ref={form}
        formConfig={{ mode: 'onSubmit' }}
        sx={{ flex: 1 }}
        formItems={[
          {
            placeholder: t('validation.email_eng_required'),
            validator: Yup.string()
              .required(t('validation.email_required'))
              .email(t('validation.email_format')),
            name: `email`,
            defaultValue: '',
            inputConfig: {
              inputBaseProps: {
                childrenSx: { flex: 1 },
              },
            },
          },
        ]}
        submitComponent={() => null}
        resetComponent={() => null}
      />

      {index > 0 && (
        <IconButton
          onClick={() => {
            onDelete?.()
          }}
        >
          <DeleteIcon />
        </IconButton>
      )}
    </Stack>
  )
})

const initialList = [{ key: 'email0', id: 0 }]

const CreateDomainDialog = ({ dispatch, isSubmitting, onClose, onComplete, ...props }) => {
  const { t } = useTranslation()

  const [createList, setCreateList] = useState(initialList)
  const itemRefs = useRef([])

  const handleClose = () => {
    setCreateList(initialList)
    itemRefs.current = []
    onClose?.()
  }

  const handleSubmit = async () => {
    // eslint-disable-next-line no-restricted-syntax
    for (const r of itemRefs.current) {
      // eslint-disable-next-line no-await-in-loop
      await r.form.trigger()
    }

    const hasError = itemRefs.current?.filter?.(r => !r.form.formState.isValid).length > 0

    if (!hasError) {
      const payload = itemRefs.current.map(r => ({
        email: r.form.getValues().email,
      }))

      dispatch({
        type: 'company/createDomain',
        payload,
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.domain_created'),
              severity: 'success',
            },
          })

          onClose?.()
        },
      })
    }
  }

  return (
    <Dialog {...props} maxWidth={'md'}>
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          color: theme => theme.palette.primary.main,
        }}
      >
        {t('general.add_domain_eng')}
      </DialogTitle>
      <DialogContent sx={{ width: 500 }}>
        <Typography mb={2}>{`${t('general.example')}: username@yourcompany.com`}</Typography>
        {createList.map((obj, i) => (
          <div key={obj.key}>
            <EmailItem
              ref={ref => {
                if (ref) {
                  itemRefs.current[i] = ref
                }
              }}
              index={i}
              id={obj.id}
              onDelete={() => {
                itemRefs.current?.splice?.(i, 1)
                setCreateList([...createList].filter(o => o.id !== obj.id))
              }}
              // onSubmit={()=>{}}
            />
          </div>
        ))}
        <Button
          sx={{ color: '#FF6700' }}
          color={'white'}
          disabled={isSubmitting}
          endIcon={<AddCircle sx={{ color: '#FF6700' }} />}
          onClick={() => {
            const latestId = createList[createList.length - 1].id + 1
            setCreateList([...createList, { key: `email${latestId}`, id: latestId }])
          }}
        >
          {t('general.add_next')}
        </Button>
      </DialogContent>
      <DialogActions sx={{ alignSelf: 'center' }}>
        <Button
          sx={{ minWidth: 150, marginRight: '30px' }}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleClose}
        >
          {t('general.cancel')}
        </Button>
        <Button
          sx={{ minWidth: 150 }}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={async () => {
            handleSubmit()
          }}
        >
          {t('general.complete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(CreateDomainDialog)
